// Types for backend models

// Re-export member profile types
export * from './memberProfile';

// Re-export guest pass types
export * from './guestPass';

// Audit Log types
export enum AuditAction {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LOGIN = 'login',
  LOGOUT = 'logout',
  PASSWORD_CHANGE = 'password_change',
  STATUS_CHANGE = 'status_change',
  MEMBERSHIP_CHANGE = 'membership_change',
  PAYMENT_PROCESSED = 'payment_processed',
  ACCESS_GRANTED = 'access_granted',
  ACCESS_DENIED = 'access_denied'
}

export enum AuditSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export interface AuditLog {
  id: string;
  userId: string;
  userEmail: string;
  userFullName: string;
  userRole: string;
  entityType: string;
  entityId: string;
  action: AuditAction;
  description: string;
  previousValues?: Record<string, any>;
  newValues?: Record<string, any>;
  changedFields?: string[];
  ipAddress?: string;
  userAgent?: string;
  requestId?: string;
  severity: AuditSeverity;
  category?: string;
  metadata?: Record<string, any>;
  isSystemAction: boolean;
  source?: string;
  createdAt: string;
}

// Class deletion response
export interface DeleteClassInstanceInfo {
  id: string;
  name: string;
  sessionDate: string;
  startTime: string;
  bookingCount: number;
}

export interface DeleteClassResponse {
  message: string;
  summary: {
    classMarkedInactive: boolean;
    instancesCancelled: number;
    futureInstancesWithMembersNotified: number;
    pastInstancesPreserved: number;
    totalMembersNotified: number;
  };
  details: {
    instancesCancelled: DeleteClassInstanceInfo[];
    futureInstancesWithBookings: DeleteClassInstanceInfo[];
    pastInstancesWithBookings: DeleteClassInstanceInfo[];
  };
}

// User related types
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  EMPLOYEE = 'employee',
  COACH = 'coach',
  MEMBER = 'member'
}

export enum UserStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  ON_HOLD = 'on_hold'
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  tenantId?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  dateOfBirth?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emailNotifications: boolean;
  hasStoredPaymentMethod: boolean;
  paymentMethods?: any;
  notes?: string;
  membershipTierId?: string;
  membershipTierName?: string;
  membershipPrice?: number;
  membershipStartDate?: string;
  membershipExpiryDate?: string;
  membershipHoldStartDate?: string;
  membershipHoldEndDate?: string;
  facilityAccessCount: number;
  lastAccessTime?: string;
  createdAt: string;
  updatedAt: string;
}

// Tenant related types
export interface Tenant {
  id: string;
  name: string;
  domain: string;
  contactEmail: string;
  contactPhone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  description?: string;
  website?: string;
  logoUrl?: string;
  databaseName: string;
  subscriptionId?: string;
  createdAt: string;
  updatedAt: string;
}

// Event related types
export enum EventStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed'
}

export enum RegistrationStatus {
  REGISTERED = 'registered',
  WAITLISTED = 'waitlisted',
  CANCELLED = 'cancelled',
  ATTENDED = 'attended',
  NO_SHOW = 'no_show'
}

export interface Event {
  id: string;
  title: string;
  description: string;
  image?: string;
  category?: string;
  eventDateTime: string; // ISO date string
  cost: number;
  maxRegistrants?: number;
  registrationStartDate?: string; // ISO date string
  registrationDeadline?: string; // ISO date string
  allowRegistrationUntilStart: boolean;
  waitlistEnabled: boolean;
  guestsAllowed: boolean;
  maxGuestsPerMember?: number;
  totalMaxGuests?: number;
  guestFee: number;
  status: EventStatus;
  location?: string;
  notes?: string;
  createdByUserId: string;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  
  // Additional computed fields
  registeredCount?: number;
  waitlistCount?: number;
  spotsRemaining?: number;
  isRegistrationOpen?: boolean;
  isUpcoming?: boolean;
  isPast?: boolean;
  isFree?: boolean;
}

export interface EventRegistration {
  id: string;
  eventId: string;
  userId: string;
  memberProfileId: string;
  memberName: string;
  memberEmail: string;
  isGuest: boolean;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  parentMemberUserId?: string;
  status: RegistrationStatus;
  waitlistPosition?: number;
  amountPaid: number;
  notes?: string;
  metadata?: {
    registrationDate?: string;
    waitlistPosition?: number;
    registrationNotes?: string;
    cancellationDate?: string;
    cancellationReason?: string;
    refundStatus?: 'pending' | 'processed' | 'failed';
    isGuest?: boolean;
    guestInfo?: {
      name: string;
      email: string;
      phone: string;
    };
    parentMemberUserId?: string;
  };
  registeredAt: string; // ISO date string
  updatedAt: string; // ISO date string
  event?: Event;
}

// API Request/Response types for events
export interface CreateEventRequest {
  title: string;
  description: string;
  category?: string;
  eventDateTime: string;
  cost: number;
  maxRegistrants?: number;
  registrationStartDate?: string;
  registrationDeadline?: string;
  allowRegistrationUntilStart: boolean;
  waitlistEnabled: boolean;
  guestsAllowed: boolean;
  maxGuestsPerMember?: number;
  totalMaxGuests?: number;
  guestFee: number;
  location?: string;
  notes?: string;
  status: EventStatus;
}

export interface UpdateEventRequest extends Partial<CreateEventRequest> {}

export interface RegisterForEventRequest {
  memberProfileId: string;
  notes?: string;
  guests?: {
    name: string;
    email: string;
    phone: string;
  }[];
}

export interface CancelRegistrationRequest {
  reason?: string;
}

export interface EventResponse {
  message: string;
  event: Event;
}

export interface EventListResponse {
  events?: Event[]; // For backward compatibility
  [index: number]: Event; // For array-like response
  length?: number;
}

export interface EventRegistrationResponse {
  message: string;
  registration: EventRegistration;
}

export interface EventRegistrationsResponse {
  registered: EventRegistration[];
  waitlisted: EventRegistration[];
  totalRegistered: number;
  totalWaitlisted: number;
}

export interface Tenant {
  id: string;
  name: string;
  identifier: string;
  databaseName: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  email?: string;
  phone?: string;
  status: 'active' | 'inactive' | 'suspended';
  settings: TenantSettings;
  createdAt: string;
  updatedAt: string;
}

// Tenant Settings
export interface TenantSettings {
  email_notifications_for_new_applications: boolean;
  admin_email_list: string[];
  tenant_heading_title: string;
  tenant_heading_subtitle: string;
  tenant_logo_img: string;
  tenant_hero_img: string;
  tenant_background_colour: string;
  cancelWithoutPenaltyHrs: number; // Hours before class to cancel without penalty
  default_booking_windows: BookingWindowConfiguration; // Default booking windows for membership tiers
  guest_access_limit: number; // Max # of times a guest can access in 30 days
  guest_fee: number; // Fee charged to member for bringing a guest
  guest_sponsorship_limit: number; // Max # of guests a member can sponsor in 30 days
  guest_pass_validity_days: number; // Number of days a guest pass is valid
  on_hold_fees: Record<string, number>; // Monthly fees per membership tier when account is on hold
  bookingOpenTime: string; // Time when booking window resets daily (HH:MM format, default: 00:01)
  daily_amenity_booking_limit: number; // Daily booking limit per amenity type in minutes (default: 120)
  prime_time_booking_limit: number; // Max peak-time minutes per amenity type per day (0 = disabled)
  receipt_organization_name: string; // Organization name for receipts/invoices
  receipt_organization_address: string; // Organization address for receipts/invoices
  receipt_gst_hst_number: string; // GST/HST registration number for receipts/invoices
}

export interface TenantSettingsEntry {
  id: string;
  key: string;
  value: any;
  createdAt: string;
  updatedAt: string;
}

// Dropdown Management types
export interface DropdownUsageInfo {
  isUsed: boolean;
  usedIn: Array<{
    id: string;
    name: string;
    type: 'class' | 'amenity';
  }>;
}

// Membership related types - Legacy interface removed

export enum ApplicationStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected'
}

export interface PaymentDetails {
  cardLast4?: string;
  cardType?: string;
  paymentMethod?: string;
  transactionId?: string;
  billingAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
}

export interface AdditionalFees {
  name: string;
  amount: number;
  description?: string;
  required: boolean;
}

export interface MembershipApplication {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  mobile?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  userId?: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  membershipTierId: string;
  membershipTierName: string;
  monthlyPrice: string | number;
  status: ApplicationStatus;
  notes?: string;
  approvedByUserId?: string;
  approvalDate?: string;
  paymentDetails?: PaymentDetails;
  additionalFees?: AdditionalFees[];
  totalAdditionalFees: string | number;
  rejectionReason?: string;
  isFormerMember?: boolean;
  isStudent?: boolean;
  corporateGroupSize?: string;
  isReferred?: boolean;
  referrerName?: string;
  reasonForJoining?: string;
  createdAt: string;
  updatedAt: string;
}

// Amenity related types
export enum PricingTier {
  OFF_PEAK = 'off-peak',
  NORMAL = 'normal',
  PEAK = 'peak'
}

export interface TierPricing {
  [PricingTier.OFF_PEAK]: number;
  [PricingTier.NORMAL]: number;  
  [PricingTier.PEAK]: number;
}

export interface Equipment {
  id: string;
  name: string;
  description?: string;
  quantity: number;
  active: boolean;
  costAmount: number;
  costUnit: number; // Duration in minutes
  maxPerBooking: number;
  createdAt: string;
  updatedAt: string;
}

export interface AmenityAdditionalInfo {
  equipmentAvailable?: boolean;
  equipmentList?: string[]; // Deprecated - use equipment[] instead
  maintenanceSchedule?: {
    nextMaintenance?: string;
    frequency?: string;
  };
  rules?: string[];
  restrictions?: string[];
  amenities?: string[];
}

export interface Amenity {
  id: string;
  name: string;
  description?: string;
  type: string; // References amenity_types.name
  location?: string;
  available: boolean;
  bookingFee?: number; // Base booking fee (can be overridden by tier pricing)
  tierPricing?: TierPricing; // Default pricing for each tier
  maxBookingsPerSlot: number;
  image?: string;
  additionalInfo?: AmenityAdditionalInfo;
  bookingWindows?: BookingWindowConfiguration;
  tierAccess?: TierAccessConfiguration;
  equipment?: Equipment[]; // Associated equipment entities
  createdAt: string;
  updatedAt: string;
}

export interface AmenityType {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AmenityBooking {
  id: string;
  amenityId: string;
  userId: string;
  startTime: string;
  endTime: string;
  status: 'pending' | 'confirmed' | 'cancelled';
  notes?: string;
  guests?: BookingGuest[];
  createdAt: string;
  updatedAt: string;
}

// Amenity scheduling types
export enum SlotStatus {
  AVAILABLE = 'available',
  UNAVAILABLE = 'unavailable',
  BOOKED = 'booked',
  ON_HOLD = 'on_hold',
  OUT_OF_SCHEDULE = 'out of schedule'
}

export interface TimeSlot {
  startTime: string; // HH:MM format in 15-minute increments
  endTime: string;   // HH:MM format in 15-minute increments
  status: SlotStatus;
  pricingTier: PricingTier;
  priceOverride?: number; // Optional price override for this specific slot
  bookedBy?: string; // Name of the member who booked this slot
  bookedByEmail?: string; // Email of the member who booked this slot
  bookingId?: string; // ID of the booking for navigation
  bookingCount?: number; // Number of bookings for this slot
  programName?: string; // Name of the coaching program (if booked for a program)
  programInstanceId?: string; // ID of the coaching program instance
  programInstanceStatus?: string; // Status of the coaching program instance (e.g., 'CANCELLED')
  coachingSessionName?: string; // Name of the coaching session (if booked for a coaching session)
  coachingSessionId?: string; // ID of the coaching session
  coachingSessionStatus?: string; // Status of the coaching session (e.g., 'CANCELLED')
}

export interface DaySchedule {
  dayOfWeek: number; // 0-6, where 0 is Sunday
  slots: TimeSlot[];
}

export interface AmenitySchedulePattern {
  frequency: 'daily' | 'weekly' | 'monthly';
  dailySchedule?: TimeSlot[]; // For daily frequency
  weeklySchedule?: DaySchedule[]; // For weekly frequency
  monthlySchedule?: {
    dayOfMonth: number;
    slots: TimeSlot[];
  }[]; // For monthly frequency
  effectiveStartDate: string;
  effectiveEndDate?: string;
}

export interface BookingGuest {
  type: 'member' | 'guest';
  memberId: string | null;
  guest: {
    name: string;
    email: string;
  } | null;
}

export interface AmenitySchedule {
  id: string;
  amenityId: string;
  name: string;
  description?: string;
  schedulePattern: AmenitySchedulePattern;
  minBookingDurationMinutes: number;
  maxConsecutiveSlots: number;
  maxBookingDurationMinutes: number;
  effectiveStartDate: string;
  effectiveEndDate?: string;
  active: boolean;
  priority: number;
  courtUseRestrictions: boolean;
  minPlayers?: number;
  maxPlayers?: number;
  availableToTiers?: string[];
  createdAt: string;
  updatedAt: string;
}

// Class related types
export interface Class {
  id: string;
  name: string;
  type: string;
  description?: string;
  coachId?: string | null;
  instructorName?: string | null;
  isGuestCoach: boolean;
  venue: string;
  level: string;
  capacity: number;
  scheduleDate: string;
  startTime: string;
  endTime: string;
  oneTimeCost?: number;
  active: boolean;
  isWaitlistAvailable?: boolean;
  recurringPattern?: RecurringPattern;
  image?: string;
  bookingWindows?: BookingWindowConfiguration;
  tierAccess?: TierAccessConfiguration;
  createdAt: string;
  updatedAt: string;
  currentBookings?: number;
  confirmedBookings?: number;
  waitlistBookings?: number;
  availableSpots?: number;
  // Legacy field for backward compatibility
  instructor?: string;
}

export interface ClassInstance {
  id: string;
  parentClassId: string;
  name: string;
  type: string;
  description?: string;
  instructor: string;
  venue: string;
  level: string;
  capacity: number;
  sessionDate: string;
  startTime: string;
  endTime: string;
  costOverride?: number;
  isWaitlistAvailable?: boolean;
  bookingWindows?: BookingWindowConfiguration;
  tierAccess?: TierAccessConfiguration;
  image?: string;
  status: string;
  active: boolean;
  isModified: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  parentClass?: Class;
  enrolledCount?: number;
  confirmedBookings?: number;
  waitlistBookings?: number;
  availableSpots?: number;
  bookings?: ClassBooking[];
}

export interface ClassType {
  id: string;
  name: string;
  description?: string;
  color?: string; // Hex color code (e.g., #FFB3BA)
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClassLevel {
  id: string;
  name: string;
  description?: string;
  sortOrder?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RecurringPattern {
  frequency: 'daily' | 'weekly' | 'monthly';
  interval: number;
  daysOfWeek?: number[];
  endDate?: string;
  occurrences?: number;
}

export interface ClassBooking {
  id: string;
  classId: string;
  classInstanceId?: string;
  memberId: string;
  memberEmail: string;
  memberName: string;
  // Legacy fields for backwards compatibility
  userId?: string;
  userEmail?: string;
  userName?: string;
  status: 'confirmed' | 'cancelled' | 'attended' | 'no_show' | 'waitlist';
  paid: boolean;
  paidAmount?: number;
  waitlistPosition?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Facility Access types
export interface FacilityAccess {
  id: string;
  userId: string;
  entryTime: string;
  exitTime?: string;
  location?: string;
  createdAt: string;
  updatedAt: string;
}

// Transaction types
export interface Transaction {
  id: string;
  memberId: string;
  billedToMemberId?: string;
  billedToGroupId?: number;
  amount: number;
  tax: number;
  discountPercentage: number;
  discountAmount: number;
  totalAmount: number;
  type: 'membership_payment' | 'amenity_booking' | 'class_booking' | 'initiation_fee' | 'guest_fee' | 'improvement_fee' | 'coaching' | 'refund' | 'other';
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'cancelled';
  description?: string;
  paymentMethod?: string;
  paymentId?: string;
  paymentGroupId?: string;
  relatedEntityId?: string;
  relatedEntityType?: string;
  transactionDate?: string;
  retryCount?: number;
  lastRetryAt?: string;
  metadata?: {
    adminCancellation?: boolean;
    adminCancellationNote?: string;
    cancelledBy?: string;
    cancelledAt?: string;
    refundNote?: string;
    refundedAt?: string;
    refundAmount?: number;
    refundTransactionId?: string;
    lastError?: string;
    lastErrorCode?: string;
    lastErrorAt?: string;
    isTerminalError?: boolean;
    rawError?: string;
    [key: string]: any;
  };
  memberProfile?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Membership tier types
export interface AdditionalPerks {
  name: string;
  description?: string;
  icon?: string;
}

// Booking window configuration for membership tiers
export interface MembershipTierBookingWindow {
  advanceBookingDays: number;
  unlimited?: boolean;
}

export interface BookingWindowConfiguration {
  [tierName: string]: MembershipTierBookingWindow;
}

// Tier access configuration for classes
export enum TierAccessLevel {
  FREE = 'free',
  PAID = 'paid',
  NO_ACCESS = 'no_access'
}

export interface TierAccessConfiguration {
  [tierName: string]: TierAccessLevel;
}

// Current MembershipTier interface matching the backend MembershipTier2 entity
export interface MembershipTier {
  id: string;
  name: string;
  monthlyPrice: number;
  description?: string;
  amenityBookingsLimit?: number;
  unlimitedAmenityBookings: boolean;
  classBookingsLimit?: number;
  unlimitedClassBookings: boolean;
  active: boolean;
  initiationFee: number;
  additionalPerks?: AdditionalPerks[];
  visible: boolean;
  durationMonths?: number;
  features?: string[];
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

// User membership types
export interface UserMembership {
  id: string;
  userId: string;
  membershipTierId: string;
  membershipTier: {
    name: string;
  },
  membershipPrice: number;
  startDate: string;
  endDate?: string;
  expiryDate?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  status: 'active' | 'inactive' | 'on_hold' | 'cancelled';
}

// Discount types
export interface Discount {
  id: string;
  name: string;
  type: 'one time' | 'ongoing';
  category: 'percentage' | 'amount';
  value: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// One-Time Fee types
export interface OneTimeFee {
  id: number;
  userId?: string;
  feeType: 'initiation' | 'activation' | 'equipment' | 'locker_rental' | 'personal_training' | 'late_fee' | 'cancellation' | 'other';
  feeName: string;
  amount: number;
  status?: 'pending' | 'paid' | 'waived' | 'cancelled';
  waivedReason?: string | null;
  dueDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

// Member Group types
export interface MemberGroup {
  id: number;
  groupName: string;
  groupType: 'family' | 'corporate';
  primaryContactId?: string | null;
  companyName?: string | null;
  billingAddress?: string | null;
  groupDiscountPercent: number;
  maxMembers?: number | null;
  isActive: boolean;
  paymentInfo?: any | null;
  chargeToGroupPrimaryContact: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export enum MemberGroupRole {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  DEPENDENT = 'dependent'
}

export interface CoachingProgram {
  id: string;
  name: string;
  type: string;
  description?: string;
  coachId?: string;
  coachName: string;
  coachEmail?: string;
  isGuestCoach: boolean;
  guestCoachName?: string;
  venues: string[]; // Array of amenity IDs
  level: string;
  capacity: number;
  startDate: string;
  endDate: string;
  cost: number;
  totalCost?: number;
  dropInRate?: number;
  dropInCost?: number;
  status: string;
  active: boolean;
  image?: string;
  notes?: string;
  enrolledCount?: number;
  availableSpots?: number;
  recurringPattern?: {
    frequency: 'weekly' | 'biweekly' | 'monthly';
    daysOfWeek: number[];
    startTime: string;
    endTime: string;
    duration?: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CoachingProgramInstance {
  id: string;
  parentProgramId: string;
  name: string;
  type: string;
  description?: string;
  coachId?: string;
  coachName: string;
  coachEmail?: string;
  venues: string[]; // Array of amenity IDs
  level: string;
  capacity: number;
  sessionDate: string;
  startTime: string;
  endTime: string;
  status: string;
  active: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  parentProgram?: CoachingProgram;
  enrolledCount?: number;
}

// GroupPlay types
export enum GroupPlayStatus {
  SCHEDULED = 'SCHEDULED',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export enum GroupPlayBookingStatus {
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  ATTENDED = 'ATTENDED',
  NO_SHOW = 'NO_SHOW',
  WAITLIST = 'WAITLIST'
}

export interface GroupPlay {
  id: string;
  name: string;
  type?: string;
  description?: string;
  coachId?: string | null;
  coachName?: string | null;
  coachEmail?: string | null;
  isGuestCoach?: boolean;
  guestCoachName?: string | null;
  venues: string[]; // Array of amenity IDs for venue reservations
  venueNames?: string[]; // Array of venue names for display (populated by backend)
  scheduleDate: string; // Initial/first occurrence date
  startTime: string;
  endTime: string;
  durationMinutes: number;
  capacity: number;
  isWaitlistAvailable: boolean;
  allowGuestBookings: boolean;
  memberSessionFee: number;
  guestSessionFee?: number;
  bookingWindows?: BookingWindowConfiguration;
  tierAccess?: TierAccessConfiguration;
  status: GroupPlayStatus;
  active: boolean;
  notes?: string;
  recurringPattern?: RecurringPattern;
  image?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GroupPlayInstance {
  id: string;
  parentGroupPlayId: string;
  name: string;
  description?: string;
  venues: string[];
  venueNames?: string[];
  sessionDate: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  capacity: number;
  isWaitlistAvailable: boolean;
  allowGuestBookings: boolean;
  memberFeeOverride?: number;
  guestFeeOverride?: number;
  bookingWindows?: BookingWindowConfiguration;
  tierAccess?: TierAccessConfiguration;
  status: GroupPlayStatus;
  notes?: string;
  isModified: boolean;
  createdAt: string;
  updatedAt: string;
  parentGroupPlay?: GroupPlay;
  confirmedCount?: number;
  waitlistCount?: number;
}

export interface GroupPlayBooking {
  id: string;
  groupPlayInstanceId: string;
  memberId?: string;
  memberName?: string;
  memberEmail?: string;
  memberProfile?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  guestName?: string;
  guestEmail?: string;
  status: GroupPlayBookingStatus;
  waitlistPosition?: number;
  transactionId?: string;
  notes?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  createdAt: string;
  updatedAt: string;
  groupPlayInstance?: GroupPlayInstance;
}
